import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Eye, UserPlus, Ticket, DollarSign, Bookmark, LogOut, ShieldCheck, RefreshCw, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { BusinessDetails } from "@/components/app/BusinessDetails";

// Owner key lives in sessionStorage only: it never survives the tab and is not shared across sessions.
const KEY_STORE = "pb_admin_key";
const LIVE_CLS = "border-emerald-400/40 text-emerald-400 bg-emerald-400/10";
const TEST_CLS = "border-oracle/40 text-oracle bg-oracle/10";

function paypalBadge(paypal) {
  if (!paypal?.enabled) return { label: "OFF", cls: "border-white/10 text-parchment/40", title: "Add PAYPAL_CLIENT_ID + PAYPAL_CLIENT_SECRET to enable" };
  const title = `${paypal.subscriptions} active subs · ${paypal.passes_sold} passes`;
  if (paypal.mode === "live") return { label: "LIVE", cls: LIVE_CLS, title };
  return { label: "SANDBOX", cls: TEST_CLS, title };
}

function purchaseSuffix(p) {
  let suffix = "";
  if (p.kind === "trial") suffix = " · trial";
  else if (p.kind === "invoice") suffix = " · renewal";
  if (p.provider === "paypal") suffix += " · PayPal";
  return suffix;
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-surface border border-white/5 rounded-xl p-5">
      <div className="label-caps flex items-center gap-2 text-parchment/60"><Icon className="w-3 h-3" strokeWidth={1.5} /> {label}</div>
      <div className="font-heading text-4xl text-parchment mt-2 tabular-nums">{value}</div>
      {sub && <div className="font-body text-xs text-oracle/80 mt-1">{sub}</div>}
    </div>
  );
}

function ActionButton({ id, busy, disabled, onClick, testid, children, className = "mt-2" }) {
  return (
    <Button data-testid={testid} variant="ghost" size="sm" disabled={disabled || busy === id} onClick={onClick} className={`${className} px-0 text-oracle hover:text-[#FFC033] hover:bg-transparent label-caps h-auto`}>
      {children}
    </Button>
  );
}

function AdminLogin({ onSubmit, loading, error }) {
  const [input, setInput] = useState("");
  return (
    <div className="min-h-screen bg-ink text-parchment flex items-center justify-center px-4">
      <form
        data-testid="admin-login-form"
        onSubmit={(e) => { e.preventDefault(); onSubmit(input.trim()); }}
        className="w-full max-w-sm bg-black/40 backdrop-blur-xl border border-oracle/30 rounded-xl p-8"
      >
        <div className="label-caps text-oracle flex items-center gap-2"><ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> Owner Access</div>
        <h1 className="font-heading text-3xl mt-2">Oracle <span className="italic text-oracle">Ledger</span></h1>
        <p className="font-body text-xs text-parchment/50 mt-2">Enter your owner key to see visitors, sign-ups and sales.</p>
        <Input
          data-testid="admin-key-input"
          type="password"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Owner key"
          className="mt-6 bg-surface border-white/10 text-parchment font-body focus-visible:ring-oracle"
        />
        {error && <div data-testid="admin-error" className="font-body text-xs text-pb_red mt-3">{error}</div>}
        <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="mt-5 w-full bg-oracle text-ink hover:bg-[#FFC033] label-caps">
          {loading ? "Checking…" : "Open ledger"}
        </Button>
        <Link to="/" className="block mt-4 text-center label-caps text-parchment/40 hover:text-oracle">Back to app</Link>
      </form>
    </div>
  );
}

function RecentPurchases({ purchases, planLabels }) {
  return (
    <div className="mt-8 bg-surface border border-white/5 rounded-xl overflow-hidden" data-testid="recent-purchases">
      <div className="grid grid-cols-12 label-caps px-6 py-3 border-b border-white/5">
        <div className="col-span-4">Date</div><div className="col-span-3">Buyer</div><div className="col-span-3">Plan</div><div className="col-span-2 text-right">Amount</div>
      </div>
      {purchases.length === 0 && <div className="px-6 py-8 text-center font-body text-parchment/40">No sales yet — share the promo video.</div>}
      {purchases.map((p) => (
        <div key={p.session_id + p.date} data-testid="recent-purchase-row" className="grid grid-cols-12 px-6 py-3 border-b border-white/5 font-body text-sm">
          <div className="col-span-4 text-parchment/70">{p.date?.replace("T", " ").slice(0, 16)} UTC</div>
          <div className="col-span-3 text-parchment/80 truncate">{p.email}</div>
          <div className="col-span-3 text-parchment/60 truncate">{planLabels?.[p.plan] || p.plan || "—"}{purchaseSuffix(p)}</div>
          <div className="col-span-2 text-right text-oracle">${Number(p.amount).toFixed(2)}</div>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem(KEY_STORE) || "");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/stats", { headers: { "X-Admin-Key": k } });
      setStats(data);
      sessionStorage.setItem(KEY_STORE, k);
    } catch (e) {
      setError(e.response?.status === 401 ? "Invalid owner key" : "Failed to load stats");
      if (e.response?.status === 401) { sessionStorage.removeItem(KEY_STORE); setKey(""); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (key) load(key); }, [key, load]);

  const logout = () => { sessionStorage.removeItem(KEY_STORE); setKey(""); setStats(null); };

  const act = async (id, path, msg) => {
    setBusy(id);
    try {
      const { data } = await api.post(path, {}, { headers: { "X-Admin-Key": key } });
      toast.success(msg(data));
      await load(key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  if (!key || !stats) return <AdminLogin onSubmit={(k) => (k === key ? load(k) : setKey(k))} loading={loading} error={error} />;

  const v = stats.visitors, s = stats.sales, u = stats.users, r = stats.reminders;
  const maxDaily = Math.max(1, ...stats.daily_new_visitors.map((d) => d.count));
  const pp = paypalBadge(stats.paypal);
  const emailOff = !r.email_configured;

  return (
    <div className="min-h-screen bg-ink text-parchment px-4 sm:px-8 lg:px-12 py-10 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps text-oracle flex items-center gap-3">
            Owner Ledger
            <span data-testid="stripe-mode-badge" className={`px-2 py-0.5 rounded-full border text-[10px] tracking-widest ${stats.stripe_mode === "live" ? LIVE_CLS : TEST_CLS}`}>
              Stripe {stats.stripe_mode === "live" ? "LIVE" : "TEST"}
            </span>
            <span data-testid="paypal-mode-badge" title={pp.title} className={`px-2 py-0.5 rounded-full border text-[10px] tracking-widest ${pp.cls}`}>
              PayPal {pp.label}
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tight">Who found the <span className="italic text-oracle">Oracle</span></h1>
          <p className="font-body text-xs text-parchment/50 mt-2">A "download" for a web app = a unique browser that opened it. Counted once per device.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="admin-refresh-btn" variant="ghost" onClick={() => load(key)} className="text-parchment hover:text-oracle hover:bg-white/5 label-caps"><RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
          <Link to="/"><Button variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps">App</Button></Link>
          <Button data-testid="admin-logout-btn" variant="ghost" onClick={logout} className="text-parchment hover:text-oracle hover:bg-white/5 label-caps"><LogOut className="w-4 h-4 mr-2" /> Lock</Button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Unique visitors (all time)" value={v.total} sub={`${v.new_last_7d} new in last 7 days`} testid="stat-visitors-total" />
        <Stat icon={Eye} label="Active last 24h / 7d" value={`${v.last_24h} / ${v.last_7d}`} sub={`${v.total_visits} total sessions`} testid="stat-visitors-active" />
        <Stat icon={UserPlus} label="Registered accounts" value={u.total} sub={`${u.new_last_7d} new in last 7 days`} testid="stat-users" />
        <Stat icon={Bookmark} label="Saved picks" value={stats.saved_picks} testid="stat-picks" />
        <Stat icon={Ticket} label="Passes & subscriptions sold" value={s.passes_sold} sub={`${s.active_passes} legacy passes active · ${s.checkouts_started} checkouts started`} testid="stat-passes" />
        <div className="bg-surface border border-white/5 rounded-xl p-5" data-testid="stat-subscriptions">
          <div className="label-caps flex items-center gap-2 text-parchment/60"><Ticket className="w-3 h-3" strokeWidth={1.5} /> Subscriptions</div>
          <div className="font-heading text-4xl text-parchment mt-2 tabular-nums">{stats.subscriptions?.active ?? 0}<span className="font-body text-xs text-parchment/40 ml-2">active</span></div>
          <div className="mt-2 space-y-1 font-body text-xs">
            <div className="flex justify-between"><span className="text-parchment/70">MRR</span><span className="text-oracle tabular-nums">${(stats.subscriptions?.mrr_usd ?? 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-parchment/70">On free trial</span><span className="text-oracle tabular-nums">{stats.subscriptions?.trialing ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-parchment/70">Payment failed</span><span className="text-pb_red tabular-nums">{stats.subscriptions?.past_due ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-parchment/70">Cancelling · cancelled</span><span className="text-parchment/70 tabular-nums">{stats.subscriptions?.canceling ?? 0} · {stats.subscriptions?.canceled ?? 0}</span></div>
          </div>
        </div>
        <div className="bg-surface border border-white/5 rounded-xl p-5" data-testid="stat-plans">
          <div className="label-caps flex items-center gap-2 text-parchment/60"><Ticket className="w-3 h-3" strokeWidth={1.5} /> Sales by plan</div>
          <div className="mt-3 space-y-1.5 font-body text-sm">
            {Object.entries(stats.plans_sold || {}).map(([k, n]) => (
              <div key={k} className="flex justify-between"><span className="text-parchment/70">{stats.plan_labels?.[k] || k}</span><span className="text-oracle tabular-nums">{n}</span></div>
            ))}
          </div>
        </div>
        <div className="bg-surface border border-white/5 rounded-xl p-5" data-testid="stat-result-alerts">
          <div className="label-caps flex items-center gap-2 text-parchment/60"><Mail className="w-3 h-3" strokeWidth={1.5} /> Result alert subscribers</div>
          <div className="font-heading text-4xl text-parchment mt-2 tabular-nums">{stats.result_alerts?.subscribers ?? 0}</div>
          <div className="font-body text-xs mt-1 text-oracle/80">{stats.result_alerts?.sent ?? 0} alert emails sent</div>
          <ActionButton id="alerts" busy={busy} disabled={emailOff} testid="send-result-alerts-btn" onClick={() => act("alerts", "/admin/send-result-alerts", (d) => `Sent ${d.sent} result alert${d.sent === 1 ? "" : "s"} for ${d.draw_date}`)}>
            Send latest-draw alerts now
          </ActionButton>
        </div>
        <Stat icon={DollarSign} label="Revenue (USD)" value={`$${s.revenue_usd.toFixed(2)}`} sub={`${s.last_30d} sales in last 30 days`} testid="stat-revenue" />
        <div className="bg-surface border border-white/5 rounded-xl p-5" data-testid="stat-reminders">
          <div className="label-caps flex items-center gap-2 text-parchment/60"><Mail className="w-3 h-3" strokeWidth={1.5} /> Expiry reminders</div>
          <div className="font-heading text-4xl text-parchment mt-2 tabular-nums">{r.sent}</div>
          <div className="font-body text-xs mt-1">
            <span className="text-oracle/80">{r.expiring_3d} passes expiring within 3 days</span>
            <span className={`ml-2 ${emailOff ? "text-pb_red" : "text-emerald-400"}`}>· email {emailOff ? "not configured" : "on"}</span>
          </div>
          <ActionButton id="rem" busy={busy} disabled={emailOff} testid="send-reminders-btn" onClick={() => act("rem", "/admin/send-reminders", (d) => `Sent ${d.sent} reminder${d.sent === 1 ? "" : "s"}`)}>
            Send due reminders now
          </ActionButton>
          <div className="font-body text-xs mt-3 pt-3 border-t border-white/5" data-testid="trial-reminders">
            <span className="text-parchment/70">Trial-ending emails sent: </span><span className="text-oracle tabular-nums">{r.trial_reminders_sent ?? 0}</span>
            <span className="text-parchment/50"> · {r.trials_ending_24h ?? 0} trial{(r.trials_ending_24h ?? 0) === 1 ? "" : "s"} ending in 24h</span>
          </div>
          <ActionButton id="trial" busy={busy} disabled={emailOff} className="mt-1" testid="send-trial-reminders-btn" onClick={() => act("trial", "/admin/send-trial-reminders", (d) => `Sent ${d.sent} trial reminder${d.sent === 1 ? "" : "s"}`)}>
            Send trial-ending emails now
          </ActionButton>
        </div>
        <div className="bg-surface border border-white/5 rounded-xl p-5" data-testid="stat-draw-sync">
          <div className="label-caps flex items-center gap-2 text-parchment/60"><RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Auto draw sync</div>
          <div className="font-heading text-2xl text-parchment mt-2">{stats.draw_sync?.latest_draw_date || "—"}</div>
          <div className="font-body text-xs mt-1 text-parchment/60">
            {stats.draw_sync?.pending ? <span className="text-oracle">Awaiting {stats.draw_sync.last_draw_date} results</span> : "Up to date"}
            {stats.draw_sync?.last_checked_at && ` · checked ${stats.draw_sync.last_checked_at.replace("T", " ").slice(5, 16)} UTC`}
          </div>
          <ActionButton id="sync" busy={busy} testid="sync-now-btn" onClick={() => act("sync", "/admin/sync-now", (d) => `Sync done · ${d.last_result?.added ?? 0} new draws`)}>
            Sync now
          </ActionButton>
        </div>
        <div className="sm:col-span-2 bg-surface border border-white/5 rounded-xl p-5" data-testid="daily-visitors-chart">
          <div className="label-caps text-parchment/60">New visitors · last 14 days</div>
          <div className="mt-4 flex items-end gap-1.5 h-24">
            {stats.daily_new_visitors.length === 0 && <div className="font-body text-xs text-parchment/40">No visitor data yet.</div>}
            {stats.daily_new_visitors.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                <div className="w-full bg-oracle/80 rounded-sm" style={{ height: `${(d.count / maxDaily) * 80}px`, minHeight: 2 }} />
                <div className="font-body text-[9px] text-parchment/40">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <BusinessDetails adminKey={key} />
      <RecentPurchases purchases={stats.recent_purchases} planLabels={stats.plan_labels} />
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
